﻿import { ComboboxEditor, Config, DataGrid, ErrorHandling, TranslationConfig } from "@serenity-is/corelib";
import { UserPreferenceStorage } from "@serenity-is/extensions";
import { gridDefaults } from "@serenity-is/sleekgrid";
import { installBenignErrorFilter } from "@sisolco/tools/Browser/installBenignErrorFilter";
import flatpickr from "flatpickr";
import "flatpickr/dist/l10n";
import { english } from "flatpickr/dist/l10n/default";
import { getLanguageList } from "./Helpers/LanguageList";
import DOMPurify from "dompurify";
import { loadQAUI } from "./QA/qaUi";
import { loadOrganisationDialog } from "./Organisation/organisationDialogs";
import { installProcessPortfolioWidgetRegistration } from "./Process/processPortfolioRegistration";
import { installCheckReminderWidgetRegistration } from "../Compliance/CheckReminders/checkReminderRegistration";
import { installDeliveryWidgetRegistration } from "../Procurement/DeliveryHoldingQueue/deliveryHoldingQueueRegistration";
import { installEquipmentCheckWidgetRegistration } from "../Assets/EquipmentCheckReminders/equipmentCheckRegistration";
import { installHireReturnWidgetRegistration } from "../Assets/HireReturns/hireReturnRegistration";
import { installProjectEditorWidgetRegistration } from "../Projects/ProjectDashboardWidgets/projectEditorWidgetRegistration";

Config.rootNamespaces.push('KeyView');

// Sisolco.QA editors (the Approval tab's QAStatusVisualisationEditor on Artifact and Deliverable) are
// registered only as a side effect of importing the QA UI barrel, which is a runtime URL esbuild cannot
// bundle. The preload alone RACES the form: on a cold browser cache a deep link such as
// /Projects/Artifact#edit/3 builds the form first, the editor is "not found" and the dialog never
// opens (reproduced 5/5). Serenity asks lazyTypeLoader for any unregistered key and accepts a promise,
// drawing a placeholder until the editor arrives - so this covers every entry point at once: grid
// pages, sub-grids and dashboard dialogs. The preload stays so a warm load never sees the placeholder.
const previousTypeLoader = Config.lazyTypeLoader;
const QA_KEY_PREFIX = "Sisolco.QA.";
Config.lazyTypeLoader = (typeKey, kind) => {
    if (kind === "editor" && typeKey?.startsWith(QA_KEY_PREFIX))
        return loadQAUI().then(qa => (qa as unknown as Record<string, unknown>)[typeKey.substring(QA_KEY_PREFIX.length)] ?? null);
    // The Organisation dialogs the project lookups' in-place add opens (see organisationDialogs.ts).
    const organisationDialog = loadOrganisationDialog(typeKey, kind);
    if (organisationDialog)
        return organisationDialog as ReturnType<NonNullable<typeof Config.lazyTypeLoader>>;
    return previousTypeLoader ? previousTypeLoader(typeKey, kind) : null;
};
void loadQAUI();

// A lookup's in-place add (the + beside it) must open its dialog as a MODAL over the form it was
// started from. Serenity opens it with `dialogOpen(this.openDialogAsPanel)`, and nothing sets that
// property, so each dialog falls back to its own default - a full-page PANEL for the Organisation
// dialogs. Started from the Project Wizard (itself a modal), the panel then sits under the wizard's
// backdrop: greyed out and unclickable, even its close button, with no way back but a page reload.
// Found in the browser pass, 2026-09-28. Set on the prototype so every combobox inherits it; corelib
// declares no own field that would shadow it.
(ComboboxEditor.prototype as unknown as { openDialogAsPanel?: boolean }).openDialogAsPanel = false;

// Offer the portfolio process widget to the dashboards runtime. Site-wide because a dashboard can be
// hosted on any page and the library exposes no per-page hook for a consumer widget module.
//
// ⚠ NOT the plain synchronous call, and the difference is not cosmetic: this module is a head module
// and the dashboards bootstrap is a body module, so at this point the library has not published its
// registry yet and a straight register() is a silent no-op — the widget then draws its title and
// nothing else. The installer falls back to DOMContentLoaded, which is ahead of the first cell.
// Either way it reads one global and makes no request, so a page with no dashboard pays nothing.
installProcessPortfolioWidgetRegistration();

// Offer the due / overdue check reminder widgets to the dashboards runtime (IM-027). Same deferral,
// same reason: this is a head module and the dashboards bootstrap is a body module, so a straight
// register() here is a silent no-op and the cell then draws its title and nothing else. One global
// read, no request, so a page with no dashboard pays nothing.
installCheckReminderWidgetRegistration();

// Offer the delivery holding-queue widget to the dashboards runtime (IM-004c). Same deferral, same
// reason as the two above: this is a head module and the dashboards bootstrap is a body module, so a
// straight register() here is a silent no-op and the cell then draws its title and nothing else.
installDeliveryWidgetRegistration();

// The two equipment-check widgets (sprint 15 rework) - same mechanism and ordering as the two above.
installEquipmentCheckWidgetRegistration();

// The two hire-return widgets (sprint 16) - same mechanism and ordering.
installHireReturnWidgetRegistration();

// The project's Process and Quality Plan as dashboard widgets (the PM Dashboard's extra pages) - same
// mechanism and ordering. Their editors are imported only when a cell first draws.
installProjectEditorWidgetRegistration();

// Grid settings (column choice, order and width, sort, filters, quick search) persist per user in the
// UserPreferences table, through Serenity.Extensions' UserPreference endpoint. They follow the user to
// any browser and survive an app recycle, and an administrator can clear them with a DELETE on that
// table. Without this, DataGrid persists nothing.
//
// On by default for every grid. To opt a grid out, override in that grid's class:
//   - gridPersistenceFlags() returning { quickFilters: false, ... } to stop persisting individual
//     settings (each flag persists unless set to false);
//   - getPersistenceStorage() returning null to persist nothing at all for that grid.
DataGrid.defaultPersistenceStorage = new UserPreferenceStorage();

TranslationConfig.getLanguageList = getLanguageList;
gridDefaults.sanitizer = (globalThis.DOMPurify = DOMPurify).sanitize;

// Flatpickr ships no en-GB locale (its `english` default starts the week on Sunday).
// Seed one so date pickers on en-GB pages start their week on Monday.
//
// Serenity.Corelib's `Base` ScriptBundle inlines its OWN flatpickr — accessible
// via `window.flatpickr` — and that's the instance Serenity's DateEditor reads.
// The `flatpickr` imported above is a separate npm-bundled copy used for this
// file's localize call. Seed `en-gb` on BOTH so DateEditor and any direct
// flatpickr usage agree on Monday-first.
const enGB = { ...english, firstDayOfWeek: 1 };
(flatpickr.l10ns as Record<string, typeof english>)["en-gb"] ??= enGB;
const globalFlatpickr = (window as unknown as { flatpickr?: { l10ns: Record<string, typeof english>; localize: (l: typeof english) => void } }).flatpickr;
if (globalFlatpickr) {
    globalFlatpickr.l10ns["en-gb"] ??= enGB;
}

let culture = (document.documentElement?.lang || 'en').toLowerCase();
if (flatpickr.l10ns[culture]) {
    flatpickr.localize(flatpickr.l10ns[culture]);
} else {
    culture = culture.split('-')[0];
    flatpickr.l10ns[culture] && flatpickr.localize(flatpickr.l10ns[culture]);
}

// Mirror the localize call on Serenity's bundled flatpickr — `flatpickr.localize`
// only mutates the imported instance's defaults; the global one DateEditor uses
// needs its own localize to pick up the en-GB Monday-first setting.
if (globalFlatpickr) {
    const globalCulture = (document.documentElement?.lang || 'en').toLowerCase();
    const locale = globalFlatpickr.l10ns[globalCulture] ?? globalFlatpickr.l10ns[globalCulture.split('-')[0]];
    if (locale) globalFlatpickr.localize(locale);
}

// The library filter drops ResizeObserver chatter by default; KeyView adds aborted fetches. Opening a
// project dialog runs cascading company lookups, and a lookup superseded by the next one is aborted and
// immediately retried - the abort is expected, but reached the reporter as an unhandled rejection and
// toasted. Matched on the browsers' fixed abort messages, not on "abort" anywhere in the text, so a
// real error that merely mentions an abort still surfaces.
installBenignErrorFilter({
    // Adapted rather than passed directly: the handler types its first argument as ErrorEvent, the
    // filter hands on the browser's wider string | Event.
    onError: (messageOrEvent, filename, lineno, colno, error) =>
        ErrorHandling.runtimeErrorHandler(messageOrEvent as any, filename, lineno, colno, error),
    onUnhandledRejection: ErrorHandling.unhandledRejectionHandler,
    extraBenignPatterns: [
        /^The user aborted a request\.?$/,
        /^signal is aborted without reason$/,
        /^The operation was aborted\.?\s*$/,
    ],
});
